import React, { useState, useEffect } from 'react';

import { isValidBirthday } from '@app/shared/utils/birthday';

const INITIAL_DATE_OF_BIRTH = { day: '', month: '', year: '' };

export function useDateOfBirthInput(
  day = INITIAL_DATE_OF_BIRTH.day,
  month = INITIAL_DATE_OF_BIRTH.month,
  year = INITIAL_DATE_OF_BIRTH.year
) {
  const [dateOfBirth, setDateOfBirth] = useState({ day, month, year });
  const [dateOfBirthErrorTranslationKey, setDateOfBirthErrorTranslationKey] = useState('');

  useEffect(() => {
    setDateOfBirth({ day, month, year });
  }, [day, month, year]);

  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>): boolean => {
    const { name, value } = e.target;

    // Allow only digits and empty value (for clearing)
    if (!/^\d*$/.test(value)) {
      return false;
    }

    // Limit length based on field
    const maxLength = name === 'year' ? 4 : 2;
    if (value.length > maxLength) {
      return false;
    }

    const num = Number(value);

    const isValidDuringTyping = (() => {
      if (value === '') {
        return true;
      }
      if (name === 'day') {
        return num >= 0 && num <= 31;
      } else if (name === 'month') {
        return num >= 0 && num <= 12;
      } else if (name === 'year') {
        return num >= 0 && num <= new Date().getFullYear();
      }
      return false;
    })();

    if (isValidDuringTyping) {
      setDateOfBirth((prevState) => ({ ...prevState, [name]: value }));
      return true;
    }

    return false;
  };

  const handleDateOfBirthClear = () => {
    setDateOfBirth(INITIAL_DATE_OF_BIRTH);
  };

  const validateDateOfBirth = () => {
    if (!dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
      setDateOfBirthErrorTranslationKey('errors.empty_birthday');
      return false;
    } else if (!isValidBirthday(+dateOfBirth.day, +dateOfBirth.month, +dateOfBirth.year)) {
      setDateOfBirthErrorTranslationKey('errors.invalid_birthday');
      return false;
    } else {
      setDateOfBirthErrorTranslationKey('');
      return true;
    }
  };

  return {
    dateOfBirth,
    setDateOfBirth,
    dateOfBirthErrorTranslationKey,
    setDateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    validateDateOfBirth,
  };
}
